import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { Helmet } from 'react-helmet';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { FileUpload } from 'primereact/fileupload';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { FilterMatchMode } from 'primereact/api';
import { Dropdown } from 'primereact/dropdown';
import { RadioButton } from 'primereact/radiobutton';
import { Divider } from 'primereact/divider';
import { Dialog } from 'primereact/dialog';
import axios from 'axios';
import MolstarViewerProjection from '../../components/MolstarViewerProjection/MolstarViewerProjection';

const API_URL = 'https://test.bioinfolab.sns.it/graphql';
const POLL_INTERVAL_MS = 1500;

const TASK_FIELDS = `
  taskId
  kind
  status
  progress
  createdAt
  expiresAt
  params
  artifacts
  error
  result
`;

const RUN_PROJECTION = `
  mutation runProjection($input: String!, $taxon: Int, $withContacts: Boolean!, $contactDistance: Int!, $target: String) {
    runProjection(input: $input, taxon: $taxon, withContacts: $withContacts, contactDistance: $contactDistance, target: $target) {
      ambiguous
      symbol
      candidates { accession entryName proteinName organism taxonId length }
      task { ${TASK_FIELDS} }
    }
  }
`;

const RUN_PROJECTION_FROM_STRUCTURE = `
  mutation runProjectionFromStructure($file: Upload!, $chain: String, $withContacts: Boolean!, $contactDistance: Int!, $target: String) {
    runProjectionFromStructure(file: $file, chain: $chain, withContacts: $withContacts, contactDistance: $contactDistance, target: $target) {
      ${TASK_FIELDS}
    }
  }
`;

const GET_TASK = `
  query projectionTask($taskId: String!) {
    projectionTask(taskId: $taskId) {
      ${TASK_FIELDS}
    }
  }
`;

const GET_ANNOTATIONS = `
  query projectionAnnotations($taskId: String!) {
    projectionAnnotations(taskId: $taskId) {
      taskId
      targetAccession
      total
      truncated
      rows
    }
  }
`;

const GET_MAP = `
  query projectionMap($taskId: String!) {
    projectionMap(taskId: $taskId) {
      length
      runs
    }
  }
`;

const GET_CONTACTS = `
  query projectionContacts($taskId: String!) {
    projectionContacts(taskId: $taskId) {
      chain
      distance
      linkedToProjection
      linkWarning
      contacts
      perPosition
    }
  }
`;

const GET_STRUCTURE = `
  query projectionStructure($taskId: String!) {
    projectionStructure(taskId: $taskId) {
      structure
    }
  }
`;

// ---------------------------------------------------------------------------
// Projection classes. Blue and amber rather than red and green, and the four
// differ in lightness as well as hue so they are told apart without colour.
// ---------------------------------------------------------------------------

const CLASS_META = {
    projected_high_confidence: { label: 'High confidence', colour: '#17507E' },
    projected_candidate: { label: 'Candidate', colour: '#B9700A' },
    human_only: { label: 'Human only', colour: '#4B5563' },
    not_projected: { label: 'Not projected', colour: '#8A94A6' }
};

const CLASS_ORDER = [
    'projected_high_confidence',
    'projected_candidate',
    'human_only',
    'not_projected'
];

const CLASS_BLURB = {
    projected_high_confidence: 'Identical residue and experimental human evidence',
    projected_candidate: 'Residue conserved, evidence weak, absent or inferred',
    human_only: 'ClinVar and AlphaMissense: never a property of the query',
    not_projected: 'Residue not conserved, or position not aligned'
};

const BAND_COLOURS = {
    mapped: '#17507E',
    gap: '#B9700A',
    unaligned: '#C7CEDB'
};

const CONTACT_SEVERITY = {
    'residue-residue': 'info',
    'ligand-interaction': 'danger',
    'modification': 'warning'
};

const RUNNING_STATES = ['queued', 'running'];

const proteinUrl = (accession) => `/explore/?target=${accession}&type=protein`;

// Each colouring mode has its own legend: switching the colouring without
// switching the key leaves the viewer explained by the wrong one.
const COLOUR_MODES = [
    { value: 'class', label: 'Projection class' },
    { value: 'map', label: 'Map state' },
    { value: 'contacts', label: 'Contacts' }
];

const MAP_STATE_META = {
    mapped: { label: 'Mapped', colour: BAND_COLOURS.mapped },
    gap: { label: 'Gap', colour: BAND_COLOURS.gap },
    unaligned: { label: 'Outside the alignment', colour: BAND_COLOURS.unaligned }
};

const CONTACT_SCALE = [
    { label: 'No contact', colour: '#E4E8EF' },
    { label: '1–2 contacts', colour: '#9BB4CE' },
    { label: '3–5 contacts', colour: '#4A7BA8' },
    { label: '6 or more', colour: '#17507E' }
];

// Mouse HSPA9, as an example of the third input type: a pasted FASTA record.
// It is also the useful case to show first - a mammalian ortholog, where most
// annotations survive the alignment and the few that do not are the point.
const EXAMPLE_FASTA = `>sp|P38647|HSPA9_MOUSE Stress-70 protein, mitochondrial OS=Mus musculus OX=10090 GN=Hspa9 PE=1 SV=3
MISASRAAAARLVGTAASRSPAAARPQDGWNGLSHEAFRFVSRRDYASEAIKGAVVGIDL
GTTNSCVAVMEGKQAKVLENAEGARTTPSVVAFTADGERLVGMPAKRQAVTNPNNTFYAT
KRLIGRRYDDPEVQKDTKNVPFKIVRASNGDAWVEAHGKLYSPSQIGAFVLMKMKETAEN
YLGHTAKNAVITVPAYFNDSQRQATKDAGQISGLNVLRVINEPTAAALAYGLDKSEDKVI
AVYDLGGGTFDISILEIQKGVFEVKSTNGDTFLGGEDFDQALLRHIVKEFKRETGVDLTK
DNMALQRVREAAEKAKCELSSSVQTDINLPYLTMDASGPKHLNMKLTRAQFEGIVTDLIK
RTIAPCQKAMQDAEVSKSDIGEVILVGGMTRMPKVQQTVQDLFGRAPSKAVNPDEAVAIG
AAIQGGVLAGDVTDVLLLDVTPLSLGIETLGGVFTKLINRNTTIPTKKSQVFSTAADGQT
QVEIKVCQGEREMAGDNKLLGQFTLIGIPPAPRGVPQIEVTFDIDANGIVHVSAKDKGTG
REQQIVIQSSGGLSKDDIENMVKNAEKYAEEDRRKKERVEAVNMAEGIIHDTETKMEEFK
DQLPADECNKLKEEISKMRALLAGKDSETGENIRQAASSLQQASLKLFEMAYKKMASERE
GSGSSGTGEQKEDQKEEKQ`;

// Served from the frontend's public folder: copy 7lfj_updated.cif to
// public/examples/ and it is fetched on click, no backend route needed.
const EXAMPLE_STRUCTURE = {
    label: '7LFJ (structure)',
    path: '/examples/7lfj_updated.cif',
    filename: '7lfj_updated.cif'
};

const EXAMPLES = [
    { label: 'P0A6F5', value: 'P0A6F5' },
    { label: 'groEL', value: 'groL' },
    { label: 'OPG126', value: 'OPG126' },
    { label: 'Mouse HSPA9 (FASTA)', value: EXAMPLE_FASTA }
];

const fmtPct = (v) => (v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`);

const fmtEvalue = (v) => {
    if (v === null || v === undefined) return '—';
    if (v === 0) return '0';
    return Number(v).toExponential(1);
};

// ---------------------------------------------------------------------------
// The residue band: every position of the submitted sequence, left to right.
// Clicking a region filters the table under it.
//
// Gap positions are counted but not placed: their exact coordinates live in
// map.tsv, which the page does not load. The legend says so rather than
// drawing them somewhere arbitrary.
// ---------------------------------------------------------------------------

const RUN_MIN_WIDTH_PX = 3;

const STATE_COLOUR = {
    mapped: BAND_COLOURS.mapped,
    gap: BAND_COLOURS.gap,
    unaligned: BAND_COLOURS.unaligned
};

// Every position of the submitted sequence, left to right, drawn from the
// real map. A single-residue gap in a 548-residue protein is 0.2% of the
// width, so each run also gets a minimum pixel width - otherwise the states
// the user most needs to see are the ones that disappear.
const ResidueBand = ({ mapData, mapSummary, selected, onSelect }) => {
    if (!mapData || !mapData.runs || !mapData.runs.length) return null;

    const total = mapData.length || 1;
    // One run means one region: clicking it would select the whole protein.
    const clickable = mapData.runs.length > 1;
    const ticks = [1, Math.round(total * 0.25), Math.round(total * 0.5), Math.round(total * 0.75), total];

    return (
        <div className="d-flex flex-column gap-2">
            <div className="d-flex" style={{ height: '34px', borderRadius: '4px', overflow: 'hidden' }}>
                {mapData.runs.map((run) => {
                    const dimmed = selected && !(selected.from === run.from && selected.to === run.to);
                    return (
                        <button
                            key={`${run.from}-${run.to}`}
                            type="button"
                            onClick={clickable ? () => onSelect(selected && selected.from === run.from ? null : run) : undefined}
                            aria-label={`Positions ${run.from} to ${run.to}, ${run.state}`}
                            aria-pressed={!!(selected && selected.from === run.from)}
                            title={`${run.from}–${run.to} · ${run.state}`}
                            disabled={!clickable}
                            style={{
                                flexGrow: run.length,
                                flexBasis: 0,
                                minWidth: `${RUN_MIN_WIDTH_PX}px`,
                                border: 'none',
                                padding: 0,
                                cursor: clickable ? 'pointer' : 'default',
                                background: STATE_COLOUR[run.state] || BAND_COLOURS.unaligned,
                                opacity: dimmed ? 0.35 : 1,
                                transition: 'opacity 120ms'
                            }}
                        />
                    );
                })}
            </div>

            <div className="d-flex justify-content-between text-secondary font-mono" style={{ fontSize: '0.75rem' }}>
                {ticks.map((t, i) => <span key={i}>{t}</span>)}
            </div>

            <div className="d-flex align-items-center flex-wrap gap-4 mt-1" style={{ fontSize: '0.85rem' }}>
                <span className="d-flex align-items-center gap-2">
                    <span style={{ width: 14, height: 14, borderRadius: 3, background: BAND_COLOURS.mapped }} />
                    Mapped {mapSummary ? mapSummary.mapped : ''}
                </span>
                <span className="d-flex align-items-center gap-2">
                    <span style={{ width: 14, height: 14, borderRadius: 3, background: BAND_COLOURS.gap }} />
                    Gap {mapSummary ? mapSummary.gap : ''}
                </span>
                <span className="d-flex align-items-center gap-2">
                    <span style={{ width: 14, height: 14, borderRadius: 3, background: BAND_COLOURS.unaligned }} />
                    Outside the alignment {mapSummary ? mapSummary.unaligned : ''}
                </span>
                <span className="ms-auto fw-bold" style={{ color: '#1F5FA8' }}>
                    {selected
                        ? `${selected.from}–${selected.to} · ${selected.state}`
                        : (clickable ? 'Click a region to filter the table' : '')}
                </span>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------

const ProjectionPage = () => {
    const toast = useRef(null);
    const pollRef = useRef(null);
    const annotationsTable = useRef(null);
    const contactsTable = useRef(null);

    const [activeIndex, setActiveIndex] = useState(0);

    const [inputText, setInputText] = useState('');
    const [taxon, setTaxon] = useState(null);
    const [withContacts, setWithContacts] = useState(true);
    const [contactDistance, setContactDistance] = useState(5);
    const [selectedFile, setSelectedFile] = useState(null);
    const [loadingExample, setLoadingExample] = useState(false);
    const [pendingTarget, setPendingTarget] = useState(null);

    const [searchTaskId, setSearchTaskId] = useState('');
    const [loading, setLoading] = useState(false);

    const [task, setTask] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [candidates, setCandidates] = useState(null);
    const [symbol, setSymbol] = useState(null);

    const [annotations, setAnnotations] = useState(null);
    const [annotationsError, setAnnotationsError] = useState(null);
    const [structure, setStructure] = useState(null);
    const [mapData, setMapData] = useState(null);
    const loadedForRef = useRef(null);
    const [humanChoice, setHumanChoice] = useState(null);
    const [showCandidates, setShowCandidates] = useState(false);
    const [showWeakCandidates, setShowWeakCandidates] = useState(false);
    const [contactsData, setContactsData] = useState(null);

    const [bandRegion, setBandRegion] = useState(null);
    const [classFilter, setClassFilter] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [selectedResidue, setSelectedResidue] = useState(null);
    const [colourBy, setColourBy] = useState('class');
    const [expandViewer, setExpandViewer] = useState(false);

    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    const result = task && task.result ? task.result : null;

    // -----------------------------------------------------------------------
    // Server calls
    // -----------------------------------------------------------------------

    const post = useCallback(async (query, variables) => {
        const response = await axios.post(API_URL, { query, variables });
        if (response.data.errors && response.data.errors.length) {
            throw new Error(response.data.errors[0].message);
        }
        return response.data.data;
    }, []);

    const loadAnnotations = useCallback(async (taskId) => {
        try {
            const data = await post(GET_ANNOTATIONS, { taskId });
            setAnnotations(data.projectionAnnotations ? data.projectionAnnotations.rows : []);
            setAnnotationsError(null);
        } catch (e) {
            setAnnotations(null);
            setAnnotationsError(e.message);
        }
    }, [post]);

    const loadMap = useCallback(async (taskId) => {
        try {
            const data = await post(GET_MAP, { taskId });
            setMapData(data.projectionMap || null);
        } catch (e) {
            setMapData(null);
        }
    }, [post]);

    const loadContacts = useCallback(async (taskId) => {
        try {
            const data = await post(GET_CONTACTS, { taskId });
            setContactsData(data.projectionContacts || null);
        } catch (e) {
            setContactsData(null);
        }
    }, [post]);

    // The structure is fetched once, on demand: projectionTask is polled every
    // second and must not carry an mmCIF file on every tick.
    const loadStructure = useCallback(async (taskId) => {
        try {
            const data = await post(GET_STRUCTURE, { taskId });
            setStructure(data.projectionStructure ? data.projectionStructure.structure : null);
        } catch (e) {
            setStructure(null);
        }
    }, [post]);

    const fetchTask = useCallback(async (taskId) => {
        const data = await post(GET_TASK, { taskId });
        const fresh = data.projectionTask;

        if (!fresh) {
            // null is its own answer: the task expired or never existed. It
            // must not look like one that is merely waiting in the queue.
            setNotFound(true);
            setTask(null);
            return null;
        }

        setNotFound(false);
        setTask(fresh);
        return fresh;
    }, [post]);

    // Polling: only while the task is in flight, and always cleared on unmount.
    useEffect(() => {
        if (!task || !RUNNING_STATES.includes(task.status)) {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            return undefined;
        }

        pollRef.current = setInterval(async () => {
            try {
                const fresh = await fetchTask(task.taskId);
                // The effect below picks the finished task up; nothing to do here.
            } catch (e) {
                // A transient failure must not kill the poll; the next tick retries.
            }
        }, POLL_INTERVAL_MS);

        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
        };
    }, [task, fetchTask, loadAnnotations, loadMap, loadContacts, loadStructure]);

    // Everything a finished task needs, fetched once per task id. This does not
    // live in the polling effect: a cached submission comes back done on the
    // first response, so the poll never runs and nothing would be loaded.
    useEffect(() => {
        if (!task || task.status !== 'done') return;
        if (loadedForRef.current === task.taskId) return;
        loadedForRef.current = task.taskId;

        loadAnnotations(task.taskId);
        loadMap(task.taskId);
        if (task.result && task.result.contacts) loadContacts(task.taskId);
        if (task.result && task.result.route === 'structure') loadStructure(task.taskId);
    }, [task, loadAnnotations, loadMap, loadContacts, loadStructure]);

    const resetResultState = () => {
        setBandRegion(null);
        setClassFilter([]);
        setSelectedContact(null);
        setSelectedResidue(null);
        setAnnotations(null);
        setAnnotationsError(null);
        setStructure(null);
        setMapData(null);
        setContactsData(null);
        loadedForRef.current = null;
        setHumanChoice(null);
        setShowCandidates(false);
        setShowWeakCandidates(false);
        setPendingTarget(null);
        setShowCandidates(false);
        setShowWeakCandidates(false);
        setPendingTarget(null);
        setGlobalFilterValue('');
        setFilters({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    };

    // Brings the submission form back, as New Search does on Contact Calculation.
    const resetSearch = () => {
        setTask(null);
        setNotFound(false);
        setCandidates(null);
        setSymbol(null);
        setInputText('');
        setTaxon(null);
        setSelectedFile(null);
        setSearchTaskId('');
        setActiveIndex(0);
        resetResultState();
    };

    const loadExampleStructure = async () => {
        setLoadingExample(true);
        try {
            const response = await fetch(EXAMPLE_STRUCTURE.path);
            if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

            const text = await response.text();
            // A dev server answers an unknown path with index.html and a 200,
            // so response.ok is not enough: without this the uploaded "cif"
            // is HTML and Foldseek reports "No structures found in given
            // input", which points nowhere near the real cause.
            if (!/^data_/m.test(text) && !text.includes('_atom_site.')) {
                throw new Error(
                    `${EXAMPLE_STRUCTURE.path} did not return an mmCIF file. `
                    + `Copy ${EXAMPLE_STRUCTURE.filename} into the frontend's public/examples/ folder.`);
            }

            setSelectedFile(new File([text], EXAMPLE_STRUCTURE.filename, { type: 'chemical/x-cif' }));
            setInputText('');
        } catch (e) {
            toast.current.show({
                severity: 'error',
                summary: 'Could not load the example',
                detail: e.message,
                life: 6000
            });
        } finally {
            setLoadingExample(false);
        }
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setCandidates(null);
        setNotFound(false);
        resetResultState();

        try {
            if (selectedFile) {
                // Multipart, as the GraphQL upload spec requires.
                const operations = {
                    query: RUN_PROJECTION_FROM_STRUCTURE,
                    variables: {
                        file: null,
                        chain: null,
                        target: null,
                        withContacts,
                        contactDistance: parseInt(contactDistance, 10)
                    }
                };
                const formData = new FormData();
                formData.append('operations', JSON.stringify(operations));
                formData.append('map', JSON.stringify({ 0: ['variables.file'] }));
                formData.append('0', selectedFile);

                const response = await axios.post(API_URL, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                if (response.data.errors && response.data.errors.length) {
                    throw new Error(response.data.errors[0].message);
                }
                setTask(response.data.data.runProjectionFromStructure);
            } else {
                const data = await post(RUN_PROJECTION, {
                    input: inputText.trim(),
                    taxon: taxon || null,
                    target: null,
                    withContacts,
                    contactDistance: parseInt(contactDistance, 10)
                });
                const submission = data.runProjection;

                if (submission.ambiguous) {
                    // A gene symbol is not a species. Nothing has been computed.
                    setCandidates(submission.candidates);
                    setSymbol(submission.symbol);
                } else {
                    setTask(submission.task);
                }
            }
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Submission failed', detail: e.message, life: 6000 });
        } finally {
            setLoading(false);
        }
    };

    const runForAccession = async (accession, target = null) => {
        setLoading(true);
        setCandidates(null);
        resetResultState();
        try {
            const data = await post(RUN_PROJECTION, {
                input: accession,
                taxon: null,
                target,
                withContacts,
                contactDistance: parseInt(contactDistance, 10)
            });
            setTask(data.runProjection.task);
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Submission failed', detail: e.message, life: 6000 });
        } finally {
            setLoading(false);
        }
    };

    const handleCandidatePick = (accession) => {
        setInputText(accession);
        runForAccession(accession);
    };

    const retrieveTaskByid = async () => {
        setLoading(true);
        setCandidates(null);
        resetResultState();
        try {
            const fresh = await fetchTask(searchTaskId.trim());
            if (fresh && fresh.status === 'done') {
                // The effect above loads the rest.
            }
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Retrieval failed', detail: e.message, life: 6000 });
        } finally {
            setLoading(false);
        }
    };

    // A different chain is a different task: the chain is part of the cache
    // key, so coming back to one already run returns the existing result
    // instead of recomputing it.
    // Changing the human reference is a new task, not a filter: the residue
    // map and every annotation come from the protein that was chosen. The
    // target is in the cache key, so going back to one already run returns it
    // without recomputing.
    const handleTargetChange = async (accession) => {
        setLoading(true);
        setShowCandidates(false);
        setShowWeakCandidates(false);
        setPendingTarget(null);
        resetResultState();
        try {
            if (selectedFile) {
                const operations = {
                    query: RUN_PROJECTION_FROM_STRUCTURE,
                    variables: {
                        file: null,
                        chain: result ? result.chain : null,
                        target: accession,
                        withContacts,
                        contactDistance: parseInt(contactDistance, 10)
                    }
                };
                const formData = new FormData();
                formData.append('operations', JSON.stringify(operations));
                formData.append('map', JSON.stringify({ 0: ['variables.file'] }));
                formData.append('0', selectedFile);

                const response = await axios.post(API_URL, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                if (response.data.errors && response.data.errors.length) {
                    throw new Error(response.data.errors[0].message);
                }
                setTask(response.data.data.runProjectionFromStructure);
            } else {
                const data = await post(RUN_PROJECTION, {
                    input: inputText.trim(),
                    taxon: taxon || null,
                    target: accession,
                    withContacts,
                    contactDistance: parseInt(contactDistance, 10)
                });
                setTask(data.runProjection.task);
            }
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Could not change the reference', detail: e.message, life: 6000 });
        } finally {
            setLoading(false);
        }
    };

    const handleChainChange = async (chainName) => {
        setLoading(true);
        resetResultState();
        try {
            const operations = {
                query: RUN_PROJECTION_FROM_STRUCTURE,
                variables: {
                    file: null,
                    chain: chainName,
                    target: null,
                    withContacts,
                    contactDistance: parseInt(contactDistance, 10)
                }
            };
            const formData = new FormData();
            formData.append('operations', JSON.stringify(operations));
            formData.append('map', JSON.stringify({ 0: ['variables.file'] }));
            formData.append('0', selectedFile);

            const response = await axios.post(API_URL, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (response.data.errors && response.data.errors.length) {
                throw new Error(response.data.errors[0].message);
            }
            setTask(response.data.data.runProjectionFromStructure);
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Could not switch chain', detail: e.message, life: 6000 });
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.current.show({ severity: 'success', summary: 'Copied', detail: 'Task ID copied', life: 2000 });
    };

    // -----------------------------------------------------------------------
    // Derived data
    // -----------------------------------------------------------------------

    const visibleAnnotations = useMemo(() => {
        if (!annotations) return [];
        return annotations.filter((row) => {
            if (classFilter.length && !classFilter.includes(row.projection_class)) return false;
            if (bandRegion) {
                const pos = row.query_pos;
                if (pos === null || pos === undefined) return bandRegion.state !== 'mapped';
                if (pos < bandRegion.from || pos > bandRegion.to) return false;
            }
            return true;
        });
    }, [annotations, classFilter, bandRegion]);

    // The manifest carries only the contact summary; the rows come from
    // contacts.json through their own query.
    // query position -> human position, from the map runs. The contact rows
    // carry map positions; the page shows the human an_position, which is the
    // identifier the rest of Atlantis uses.
    const humanPositionOf = useMemo(() => {
        if (!mapData || !mapData.runs) return () => null;
        const index = new Map();
        mapData.runs.forEach((run) => {
            if (run.target_from === null || run.target_from === undefined) return;
            for (let p = run.from; p <= run.to; p += 1) {
                index.set(p, run.target_from + (p - run.from));
            }
        });
        return (p) => (p === null || p === undefined ? null : index.get(p) ?? null);
    }, [mapData]);

    const targetAccession = result && result.selected_target ? result.selected_target.accession : null;

    const contactRows = useMemo(() => {
        if (!contactsData || !contactsData.contacts) return [];
        return contactsData.contacts;
    }, [contactsData]);

    const molstarPayload = useMemo(() => {
        if (!selectedContact) {
            return { positions: [], chains: [], residues: [], distance: null };
        }
        return {
            positions: [selectedContact.num_a, selectedContact.num_b],
            chains: [selectedContact.chain_a, selectedContact.chain_b],
            residues: [selectedContact.res_a, selectedContact.res_b],
            distance: selectedContact.distance
        };
    }, [selectedContact]);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        setFilters({ global: { value, matchMode: FilterMatchMode.CONTAINS } });
        setGlobalFilterValue(value);
    };

    const clearFilter = () => {
        setFilters({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
        setGlobalFilterValue('');
        setClassFilter([]);
        setBandRegion(null);
    };

    // -----------------------------------------------------------------------
    // Cell templates
    // -----------------------------------------------------------------------

    const classBody = (row) => {
        const meta = CLASS_META[row.projection_class];
        if (!meta) return row.projection_class;
        return (
            <span className="d-inline-flex align-items-center gap-2">
                <Tag
                    value={meta.label}
                    rounded
                    style={{ backgroundColor: meta.colour, color: '#FFFFFF', fontSize: '0.72rem' }}
                />
                {row.alignment_conflict && (
                    <i
                        className="pi pi-exclamation-triangle"
                        style={{ color: '#B9700A', fontSize: '0.9rem' }}
                        title="The two engines disagree about this position"
                    />
                )}
            </span>
        );
    };

    const queryPosBody = (row) => {
        if (row.query_pos === null || row.query_pos === undefined) {
            return <span className="text-secondary">not aligned</span>;
        }
        return <span className="font-mono">{row.query_pos} {row.query_aa}</span>;
    };

    const humanPosBody = (row) => (
        <span className="font-mono" style={{ color: 'var(--blue-600)' }}>{row.human_pos} {row.human_aa}</span>
    );

    const annotationBody = (row) => {
        if (row.field === 'clinvar') {
            return <span><span className="fw-bold">ClinVar</span> · {row.detail} {row.type}</span>;
        }
        return (
            <span>
                <span className="fw-bold">{row.label}</span>
                {row.type ? ` · ${row.type}` : ''}
                {row.detail ? ` · ${row.detail}` : ''}
            </span>
        );
    };

    const evidenceBody = (row) => {
        if (row.field === 'clinvar') {
            const am = row.alphamissense_class
                ? ` · AlphaMissense ${row.alphamissense_score} ${row.alphamissense_class}`
                : '';
            return <small className="text-secondary">{row.clinical_significance}{am}</small>;
        }
        if (row.projection_class === 'not_projected') {
            const reason = row.reason === 'residue_not_conserved'
                ? `Residue not conserved: ${row.human_aa} in human, ${row.query_aa || '—'} in the query`
                : 'This position did not align';
            return <small className="text-secondary">{reason}</small>;
        }
        if (!row.evidence || !row.evidence.length) {
            return <small className="text-secondary">No evidence code available</small>;
        }
        return (
            <small className="text-secondary">
                {row.evidence.map((e) => `${e.eco || ''} ${e.label || ''}${e.source ? ` · ${e.source}` : ''}`).join('; ')}
            </small>
        );
    };

    const contactTypeBody = (row) => (
        <Tag value={row.category} severity={CONTACT_SEVERITY[row.category] || 'info'} rounded />
    );

    // A side that belongs to another chain has no position in this map, and a
    // ligand has no position in the sequence at all. Both would otherwise be
    // an unexplained empty cell, so each says which it is.
    const contactSideBody = (row, side) => {
        const pos = side === 'a' ? row.query_pos_a : row.query_pos_b;
        const chain = side === 'a' ? row.chain_a : row.chain_b;
        const res = side === 'a' ? row.res_a : row.res_b;

        if (pos !== null && pos !== undefined) {
            const human = humanPositionOf(pos);
            if (human === null || !targetAccession) {
                return <span className="font-mono">{pos}</span>;
            }
            return (
                <a
                    href={proteinUrl(targetAccession)}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono"
                    title={`Query position ${pos}`}
                >
                    {targetAccession}_{human}
                </a>
            );
        }
        if (row.interface === 'inter' && contactsData && chain !== contactsData.chain) {
            return <small className="text-secondary">chain {chain} · interface</small>;
        }
        if (row.category === 'ligand-interaction') {
            return <small className="text-secondary">{res} · ligand</small>;
        }
        return <small className="text-secondary">not in the map</small>;
    };

    const contactViewBody = (row) => (
        <Button
            icon="pi pi-eye"
            rounded
            text
            severity="info"
            aria-label="Show this contact in the 3D viewer"
            onClick={() => setSelectedContact(row)}
        />
    );

    // -----------------------------------------------------------------------
    // Sections
    // -----------------------------------------------------------------------

    const renderStatusPanel = () => {
        if (!task) return null;

        if (task.status === 'queued') {
            return (
                <Card className="mb-4">
                    <div className="d-flex align-items-center gap-3">
                        <i className="pi pi-clock" style={{ fontSize: '1.4rem', color: '#8A94A6' }} />
                        <div>
                            <div className="fw-bold">Queued</div>
                            <small className="text-secondary">Another task is ahead of this one.</small>
                        </div>
                        <Button label="New Search" icon="pi pi-refresh" text className="ms-auto" onClick={resetSearch} />
                    </div>
                </Card>
            );
        }

        if (task.status === 'running') {
            return (
                <Card className="mb-4">
                    <div className="d-flex align-items-center gap-3 mb-3">
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.4rem', color: '#1F6FB2' }} />
                        <div>
                            <div className="fw-bold">{task.progress || 'Running'}</div>
                        </div>
                    </div>
                    <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
                </Card>
            );
        }

        if (task.status === 'failed') {
            return (
                <div className="mb-4">
                    <Message severity="error" className="w-100 justify-content-start" text={task.error || 'The projection failed.'} />
                    <Button label="New Search" icon="pi pi-refresh" outlined className="mt-3" onClick={resetSearch} />
                </div>
            );
        }

        if (task.status === 'interrupted') {
            return (
                <div className="mb-4">
                    <Message
                        severity="warn"
                        className="w-100 justify-content-start"
                        text="The worker restarted mid-run. Submit it again."
                    />
                    <Button label="New Search" icon="pi pi-refresh" outlined className="mt-3" onClick={resetSearch} />
                </div>
            );
        }

        return null;
    };

    // The query is, or closely resembles, a protein Atlantis already holds.
    // Offered as a choice rather than a notice: someone may well have
    // submitted it on purpose, to inspect the alignment.
    const renderHumanQueryChoice = () => {
        const hq = result.human_query;
        return (
            <Card className="mb-4" style={{ borderLeft: '4px solid #1F6FB2' }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="pi pi-info-circle" style={{ color: '#1F6FB2' }} />
                    <span className="fw-bold">{hq.reason}</span>
                </div>
                <Button
                    className="w-100 mb-2 p-button-outlined text-start"
                    onClick={() => window.open(proteinUrl(hq.accession), '_blank', 'noopener')}
                >
                    <span className="font-mono me-3" style={{ minWidth: '90px', display: 'inline-block' }}>{hq.accession}</span>
                    <span className="text-secondary">Atlantis page — the annotations themselves</span>
                </Button>
                <Button
                    className="w-100 p-button-outlined text-start"
                    onClick={() => setHumanChoice('projection')}
                >
                    <span className="me-3" style={{ minWidth: '90px', display: 'inline-block' }}>Projection</span>
                    <span className="text-secondary">The alignment and what survives it</span>
                </Button>
            </Card>
        );
    };

    const renderTaskBar = () => (
        <div className="mb-4 p-3 bg-light border-round">
            <div className="row align-items-center">
                <div className="col-md-6 d-flex align-items-center gap-2">
                    <span className="fw-bold">Task ID:</span>
                    <span className="text-primary font-mono">{task.taskId}</span>
                    <Button
                        icon="pi pi-copy"
                        rounded
                        text
                        severity="secondary"
                        size="small"
                        tooltip="Copy Task ID"
                        onClick={() => copyToClipboard(task.taskId)}
                    />
                </div>
                <div className="col-md-6 text-md-end d-flex align-items-center justify-content-md-end gap-3">
                    <small className="text-secondary">
                        Results stored until {new Date(task.expiresAt).toLocaleDateString()}
                    </small>
                    <Button icon="pi pi-refresh" severity="info" rounded tooltip="New Search" onClick={resetSearch} />
                </div>
            </div>
            {result && (
                <div className="mt-2 d-flex flex-wrap gap-4" style={{ fontSize: '0.85rem' }}>
                    <span>
                        <span className="text-secondary">Query</span>{' '}
                        <span className="font-mono">
                            {result.query_header || (result.input_entry && result.input_entry.accession) || result.chain || '—'}
                        </span>
                        {result.map_summary ? ` · ${result.map_summary.query_length} residues` : ''}
                    </span>
                    <span>
                        <span className="text-secondary">Route</span>{' '}
                        {result.route === 'structure' ? 'structure · Foldseek + MMseqs2' : 'sequence · MMseqs2'}
                    </span>
                    <span><span className="text-secondary">Best hit</span> {result.selected_target_rule}</span>
                </div>
            )}
        </div>
    );

    const renderSelectedTarget = () => {
        const best = result.selected_target;
        const candidates = result.candidate_hits || [];
        const structureRoute = result.route === 'structure';
        // The heading must not assert a human reference while the banner above
        // says no alignment is significant.
        const belowThreshold = result.target_match_status === 'below_significance_threshold';

        return (
            <>
                {result.any_significant === false && (
                    <Message
                        severity="warn"
                        className="w-100 justify-content-start mb-3"
                        text={`No hit reaches an e-value of 1e-3. The alignments below are at or near chance level, `
                            + `and annotations projected from them should not be read as evidence about this protein.`}
                    />
                )}

                <div className="row g-3 mb-3">
                    <div className="col-12 col-md-8">
                        <Card className="h-100">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <span
                                    className="text-uppercase fw-bold"
                                    style={{
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.6px',
                                        color: belowThreshold ? '#B9700A' : 'var(--text-color-secondary)'
                                    }}
                                >
                                    {belowThreshold
                                        ? 'Best available match — below threshold'
                                        : 'Selected human reference'}
                                </span>
                                {candidates.length > 1 && (
                                    <Button
                                        label={showCandidates ? 'Hide candidates' : 'Change human reference'}
                                        icon="pi pi-sync"
                                        text
                                        size="small"
                                        onClick={() => setShowCandidates((v) => !v)}
                                    />
                                )}
                            </div>
                            <div className="d-flex align-items-baseline gap-3 mb-3">
                                <a href={proteinUrl(best.accession)} className="font-mono fw-bold" style={{ fontSize: '1.4rem', textDecoration: 'none' }}>
                                    {best.accession}
                                </a>
                                {result.input_entry && result.input_entry.protein_name && (
                                    <span>{result.input_entry.protein_name}</span>
                                )}
                                {result.selection_method === 'user_selected' && (
                                    <Tag value="chosen" rounded severity="warning" style={{ fontSize: '0.7rem' }} />
                                )}
                                {best.significant === false && (
                                    <Tag value="not significant" rounded severity="danger" style={{ fontSize: '0.7rem' }} />
                                )}
                            </div>
                            {belowThreshold && (
                                <div className="mb-3" style={{ fontSize: '0.85rem', color: '#B9700A' }}>
                                    No reliable human reference was identified.
                                </div>
                            )}
                            <div className="d-flex flex-wrap gap-4" style={{ fontSize: '0.85rem' }}>
                                <span><span className="text-secondary">Identity</span> {fmtPct(best.fident)}</span>
                                <span><span className="text-secondary">E-value</span> {fmtEvalue(best.evalue)}</span>
                                <span><span className="text-secondary">Bitscore</span> {best.bits}</span>
                                {best.qstart && (
                                    <span>
                                        <span className="text-secondary">Aligned</span> {best.qstart}–{best.qend} → {best.tstart}–{best.tend}
                                    </span>
                                )}
                                <span>
                                    <span className="text-secondary">Candidates</span> {candidates.length}
                                </span>
                            </div>
                        </Card>
                    </div>
                    <div className="col-12 col-md-4">
                        <Card className="h-100">
                            <div className="text-uppercase text-secondary fw-bold mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.6px' }}>
                                Mapped coverage
                            </div>
                            <div className="fw-bold" style={{ fontSize: '1.9rem', lineHeight: 1 }}>
                                {fmtPct(result.map_summary ? result.map_summary.coverage : null)}
                            </div>
                            {result.map_summary && (
                                <small className="text-secondary">
                                    {result.map_summary.mapped} of {result.map_summary.query_length} query residues have a human counterpart
                                </small>
                            )}
                        </Card>
                    </div>
                </div>

                {result.target_selection && result.target_selection.engines_agree_on_top === false && (
                    <Message
                        severity="warn"
                        className="w-100 justify-content-start mb-3"
                        text={`Foldseek and MMseqs2 disagree on the top human reference: `
                            + `${result.target_selection.foldseek_top} and ${result.target_selection.mmseqs_top}. `
                            + `The projection follows ${result.primary_engine}; both are in the candidate list.`}
                    />
                )}

                <Dialog
                    header="Below the significance threshold"
                    visible={!!pendingTarget}
                    style={{ width: '32rem' }}
                    onHide={() => setPendingTarget(null)}
                    footer={
                        <div className="d-flex justify-content-end gap-2">
                            <Button label="Cancel" outlined onClick={() => setPendingTarget(null)} />
                            <Button
                                label="Project anyway"
                                severity="warning"
                                onClick={() => {
                                    const accession = pendingTarget.accession;
                                    setPendingTarget(null);
                                    handleTargetChange(accession);
                                }}
                            />
                        </div>
                    }
                >
                    {pendingTarget && (
                        <p className="mb-0">
                            <span className="font-mono">{pendingTarget.accession}</span> aligns at an e-value of{' '}
                            {fmtEvalue(pendingTarget.evalue
                                || pendingTarget.foldseek_evalue
                                || pendingTarget.mmseqs_evalue)}, above the {fmtEvalue(result.significance_threshold)}{' '}
                            threshold. The projection will be exploratory and is not evidence of homology.
                        </p>
                    )}
                </Dialog>

                {showCandidates && (
                    <Card className="mb-4">
                        <p className="text-secondary mb-3" style={{ fontSize: '0.85rem' }}>
                            Human proteins found by sequence and structure search, ranked by alignment evidence.
                            Alignment alone cannot establish which candidate is the true ortholog; orthology support
                            is shown separately and does not affect the ranking.
                            {result.orthology && result.orthology.consulted && !result.orthology.available && (
                                <> {result.orthology.reason}.</>
                            )}
                        </p>
                        <DataTable
                            value={showWeakCandidates ? candidates : candidates.filter((c) => c.significant !== false)}
                            size="small"
                            emptyMessage="No other candidate"
                            footer={
                                candidates.some((c) => c.significant === false) ? (
                                    <Button
                                        label={showWeakCandidates
                                            ? 'Hide candidates below the threshold'
                                            : `Show ${candidates.filter((c) => c.significant === false).length} candidates below the threshold`}
                                        text
                                        size="small"
                                        onClick={() => setShowWeakCandidates((v) => !v)}
                                    />
                                ) : null
                            }
                        >
                            <Column header="#" body={(r) => r.rank} style={{ width: '60px' }} />
                            <Column header="Human protein" body={(r) => (
                                <span className="d-inline-flex align-items-center gap-2">
                                    <a href={proteinUrl(r.accession)} target="_blank" rel="noreferrer" className="font-mono">{r.accession}</a>
                                    {r.significant === false && (
                                        <i className="pi pi-exclamation-triangle" style={{ color: '#B9700A', fontSize: '0.8rem' }}
                                           title="e-value above 1e-3: at or near chance level" />
                                    )}
                                </span>
                            )} />
                            {structureRoute ? (
                                <Column header="Foldseek" body={(r) => (
                                    r.foldseek_rank
                                        ? <span>#{r.foldseek_rank} · {fmtPct(r.foldseek_fident)} · {r.foldseek_bits} bits · {fmtEvalue(r.foldseek_evalue)}</span>
                                        : <small className="text-secondary">not found</small>
                                )} />
                            ) : (
                                <Column header="Identity" body={(r) => fmtPct(r.fident)} />
                            )}
                            {structureRoute ? (
                                <Column header="MMseqs2" body={(r) => (
                                    r.mmseqs_rank
                                        ? <span>#{r.mmseqs_rank} · {fmtPct(r.mmseqs_fident)} · {r.mmseqs_bits} bits · {fmtEvalue(r.mmseqs_evalue)}</span>
                                        : <small className="text-secondary">not found</small>
                                )} />
                            ) : (
                                <Column header="Bitscore" body={(r) => r.bits} />
                            )}
                            <Column header="Alignment span" sortable sortField="query_coverage" body={(r) => fmtPct(r.query_coverage)} style={{ width: '140px' }} />
                            <Column header="Orthology" body={(r) => {
                                const o = r.orthology_support;
                                if (!o) return <small className="text-secondary">—</small>;
                                const tag = (
                                    <Tag
                                        value={`OMA${o.rel_type ? ` ${o.rel_type}` : ''}`}
                                        rounded
                                        severity="success"
                                        style={{ fontSize: '0.7rem' }}
                                    />
                                );
                                return o.url ? (
                                    <a href={o.url} target="_blank" rel="noreferrer"
                                       title={o.rel_type_meaning || 'Orthology inferred by OMA'}
                                       style={{ textDecoration: 'none' }}>
                                        {tag}
                                    </a>
                                ) : (
                                    <span title={o.rel_type_meaning || 'Orthology inferred by OMA'}>{tag}</span>
                                );
                            }} style={{ width: '120px' }} />
                            {structureRoute && (
                                <Column header="Agreement" body={(r) => (
                                    r.both_engines
                                        ? <Tag value="both engines" rounded severity="info" style={{ fontSize: '0.7rem' }} />
                                        : <small className="text-secondary">one engine</small>
                                )} style={{ width: '140px' }} />
                            )}
                            <Column header="" body={(r) => {
                                if (r.accession === best.accession) {
                                    return <small className="text-secondary">current</small>;
                                }
                                // A candidate below threshold can still be
                                // explored, but not by the same gesture as a
                                // supported one.
                                return r.significant === false ? (
                                    <Button
                                        label="Use anyway"
                                        size="small"
                                        outlined
                                        severity="warning"
                                        onClick={() => setPendingTarget(r)}
                                    />
                                ) : (
                                    <Button label="Use" size="small" outlined onClick={() => handleTargetChange(r.accession)} />
                                );
                            }} style={{ width: '120px' }} />
                        </DataTable>
                    </Card>
                )}
            </>
        );
    };

    const renderCounts = () => {
        const counts = result.projection_counts || {};
        return (
            <div className="row g-3 mb-4">
                {CLASS_ORDER.map((key) => (
                    <div className="col-6 col-md-3" key={key}>
                        <Card className="h-100" style={{ borderTop: `4px solid ${CLASS_META[key].colour}` }}>
                            <div className="fw-bold" style={{ fontSize: '1.9rem', lineHeight: 1 }}>{counts[key] || 0}</div>
                            <div className="fw-bold mt-2">{CLASS_META[key].label}</div>
                            <small className="text-secondary">{CLASS_BLURB[key]}</small>
                        </Card>
                    </div>
                ))}
            </div>
        );
    };

    // The positions a functional site is made of, so each square can say which
    // residue it is instead of being an anonymous block.
    const sitePositions = (groupKey) => {
        if (!annotations) return null;
        const rows = annotations.filter((a) => a.group === groupKey);
        if (!rows.length) return null;
        const seen = new Map();
        rows.forEach((r) => {
            if (!seen.has(r.human_pos)) {
                seen.set(r.human_pos, {
                    human_pos: r.human_pos,
                    human_aa: r.human_aa,
                    query_pos: r.query_pos,
                    query_aa: r.query_aa,
                    conserved: r.identical === true
                });
            }
        });
        return Array.from(seen.values()).sort((a, b) => a.human_pos - b.human_pos);
    };

    const renderGroups = () => {
        if (!result.projection_groups || !result.projection_groups.length) return null;
        return (
            <Card title="Functional sites" className="mb-4">
                {result.projection_groups.map((g) => (
                    <div
                        key={g.group}
                        className="d-flex align-items-center flex-wrap gap-3 p-3 mb-2 rounded"
                        style={{
                            background: g.complete ? '#F4F8FC' : '#FBF5EC',
                            border: `1px solid ${g.complete ? '#DBE2EC' : '#EBD9BF'}`
                        }}
                    >
                        <div className="flex-grow-1">
                            <div className="fw-bold">{g.group.split(':')[0]}</div>
                            <small className="text-secondary font-mono">{g.group.substring(g.group.indexOf(':') + 1)}</small>
                        </div>
                        <div className="d-flex gap-1">
                            {(sitePositions(g.group)
                                || Array.from({ length: g.human_positions }).map((_, i) => ({
                                    human_pos: i, human_aa: '', query_pos: null, query_aa: '',
                                    conserved: i < g.conserved
                                }))
                            ).map((p, i) => (
                                <button
                                    key={`${p.human_pos}-${i}`}
                                    type="button"
                                    title={p.query_pos
                                        ? `human ${p.human_aa}${p.human_pos} → query ${p.query_aa}${p.query_pos}`
                                        : `human ${p.human_aa}${p.human_pos} — not aligned`}
                                    onClick={p.query_pos && structure ? () => {
                                        setSelectedContact(null);
                                        setSelectedResidue({
                                            query_pos: p.query_pos,
                                            query_aa: p.query_aa,
                                            human_pos: p.human_pos,
                                            label: g.group.split(':')[0]
                                        });
                                    } : undefined}
                                    style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: 3,
                                        border: 'none',
                                        padding: 0,
                                        cursor: p.query_pos && structure ? 'pointer' : 'default',
                                        background: p.conserved
                                            ? CLASS_META.projected_high_confidence.colour
                                            : CLASS_META.not_projected.colour
                                    }}
                                />
                            ))}
                        </div>
                        <span className="fw-bold">{g.conserved} of {g.human_positions} conserved</span>
                    </div>
                ))}
            </Card>
        );
    };

    const renderAgreement = () => {
        if (!result || result.same_target === null || result.same_target === undefined) return null;

        if (!result.same_target) {
            return (
                <Card title="Engine agreement" className="mb-4">
                    <Message
                        severity="warn"
                        className="w-100 justify-content-start"
                        text={`The two engines landed on different human proteins: Foldseek on ${result.foldseek_best.accession}, MMseqs2 on ${result.mmseqs_best.accession}. Position-by-position agreement is not meaningful here, and the projection follows Foldseek.`}
                    />
                </Card>
            );
        }

        const ag = result.agreement;
        if (!ag) return null;

        return (
            <Card title="Engine agreement" className="mb-4">
                <div className="row g-3 mb-3">
                    <div className="col-12 col-md-4">
                        <div className="border rounded p-3 h-100">
                            <small className="text-secondary fw-bold">Foldseek · 3Di structural alignment</small>
                            <div className="d-flex align-items-baseline gap-2 mt-2">
                                <span className="font-mono fw-bold" style={{ fontSize: '1.1rem' }}>{result.foldseek_best.accession}</span>
                                <small className="text-secondary">
                                    {fmtPct(result.foldseek_best.fident)} · {result.foldseek_best.bits} bits · {result.n_hits_foldseek} hits
                                </small>
                            </div>
                            <small className="text-secondary">Leads the projection</small>
                        </div>
                    </div>
                    <div className="col-12 col-md-4">
                        <div className="border rounded p-3 h-100">
                            <small className="text-secondary fw-bold">MMseqs2 · sequence alignment</small>
                            <div className="d-flex align-items-baseline gap-2 mt-2">
                                <span className="font-mono fw-bold" style={{ fontSize: '1.1rem' }}>{result.mmseqs_best.accession}</span>
                                <small className="text-secondary">
                                    {fmtPct(result.mmseqs_best.fident)} · {result.mmseqs_best.bits} bits · {result.n_hits_mmseqs} hits
                                </small>
                            </div>
                            <small className="text-secondary">Second opinion</small>
                        </div>
                    </div>
                    <div className="col-12 col-md-4">
                        <div className="border rounded p-3 h-100">
                            <div className="fw-bold" style={{ fontSize: '1.9rem', lineHeight: 1 }}>{fmtPct(ag.agreement)}</div>
                            <div className="fw-bold mt-1">Positions agreed</div>
                            <small className="text-secondary">{ag.agree} of {ag.mapped_by_both} mapped by both</small>
                        </div>
                    </div>
                </div>

                {ag.disagree > 0 && (
                    <DataTable
                        value={ag.conflicts}
                        size="small"
                        paginator
                        rows={5}
                        header={`${ag.disagree} position${ag.disagree === 1 ? '' : 's'} where the two disagree`}
                        emptyMessage="No disagreement"
                    >
                        <Column header="Query" body={(r) => <span className="font-mono">{r.query_pos} {r.query_aa}</span>} />
                        <Column header="Foldseek" body={(r) => <span className="font-mono">{r.target_pos_a}</span>} />
                        <Column header="MMseqs2" body={(r) => <span className="font-mono">{r.target_pos_b}</span>} />
                        <Column
                            header="Reading"
                            body={(r) => (
                                <small className="text-secondary">
                                    {Math.abs(r.target_pos_a - r.target_pos_b) === 1
                                        ? 'Gap placed one position apart'
                                        : 'Local alignment differs'}
                                </small>
                            )}
                        />
                    </DataTable>
                )}
            </Card>
        );
    };

    const renderChainPicker = () => {
        if (!result || !result.chain_groups || result.chain_groups.length === 0) return null;

        const options = result.chain_groups.map((g) => ({
            label: g.n_identical > 1
                ? `${g.representative} · ${g.length} aa · ${g.n_identical} identical chains`
                : `${g.representative} · ${g.length} aa`,
            value: g.representative,
            group: g
        }));

        const current = result.chain_groups.find((g) => g.chains.includes(result.chain));

        return (
            <Card className="mb-4">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <span className="text-uppercase text-secondary fw-bold" style={{ fontSize: '0.75rem', letterSpacing: '0.6px' }}>
                        Chain
                    </span>
                    <Dropdown
                        value={result.chain}
                        options={options}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => e.value && handleChainChange(e.value)}
                        style={{ minWidth: '340px' }}
                    />
                    {result.input_entry && result.input_entry.afdb_entry_id && (
                        <small className="text-secondary">
                            {result.input_entry.afdb_entry_id}
                            {result.input_entry.afdb_tool_used ? ` · ${result.input_entry.afdb_tool_used}` : ''}
                            {result.input_entry.afdb_provider_id ? ` · ${result.input_entry.afdb_provider_id}` : ''}
                            {result.input_entry.afdb_plddt ? ` · pLDDT ${result.input_entry.afdb_plddt}` : ''}
                        </small>
                    )}
                    {current && current.n_identical > 1 && (
                        <small className="text-secondary font-mono">
                            {current.chains.join(', ')}
                        </small>
                    )}
                </div>
            </Card>
        );
    };

    const annotationsHeader = (
        <div className="row align-items-center">
            <div className="col-12 col-md-6 mb-2 mb-md-0 d-flex align-items-center gap-2">
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
                </IconField>
                <Button type="button" icon="pi pi-filter-slash" label="Clear" outlined onClick={clearFilter} />
            </div>
            <div className="col-12 col-md-6 d-flex justify-content-end align-items-center gap-2">
                <Button icon="pi pi-file" rounded tooltip="Export CSV" onClick={() => annotationsTable.current.exportCSV()} />
                <Button icon="pi pi-refresh" severity="info" rounded tooltip="New Search" onClick={resetSearch} />
            </div>
        </div>
    );

    const renderClassChips = () => {
        const counts = result.projection_counts || {};
        return (
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                {CLASS_ORDER.map((key) => {
                    const active = classFilter.includes(key);
                    return (
                        <Button
                            key={key}
                            label={`${CLASS_META[key].label} ${counts[key] || 0}`}
                            rounded
                            size="small"
                            outlined={!active}
                            style={
                                active
                                    ? { backgroundColor: CLASS_META[key].colour, borderColor: CLASS_META[key].colour }
                                    : { borderColor: CLASS_META[key].colour, color: CLASS_META[key].colour }
                            }
                            onClick={() => setClassFilter(active ? classFilter.filter((c) => c !== key) : [...classFilter, key])}
                        />
                    );
                })}
                <small className="text-secondary ms-auto">
                    {bandRegion
                        ? `Filtered to positions ${bandRegion.from}–${bandRegion.to}`
                        : `${annotations ? annotations.length : 0} annotated positions`}
                </small>
            </div>
        );
    };

    const renderAnnotations = () => {
        if (annotationsError) {
            return (
                <Card title="Projected annotations" className="mb-4">
                    <Message
                        severity="warn"
                        className="w-100 justify-content-start"
                        text={`The annotation rows could not be loaded: ${annotationsError}`}
                    />
                </Card>
            );
        }

        return (
            <Card title="Projected annotations" className="mb-4">
                {renderClassChips()}
                <DataTable
                    ref={annotationsTable}
                    value={visibleAnnotations}
                    paginator
                    rows={5}
                    filters={filters}
                    globalFilterFields={['label', 'type', 'detail', 'disease', 'clinical_significance']}
                    header={annotationsHeader}
                    emptyMessage={
                        bandRegion && bandRegion.state !== 'mapped'
                            ? `Positions ${bandRegion.from}–${bandRegion.to} have no human counterpart, so nothing can be projected onto them.`
                            : 'No annotation matches the current filters'
                    }
                    size="small"
                    removableSort
                    loading={!annotations}
                >
                    <Column header="Query" body={queryPosBody} sortable sortField="query_pos" style={{ width: '110px' }} />
                    <Column header="Human" body={humanPosBody} sortable sortField="human_pos" style={{ width: '110px' }} />
                    <Column header="Annotation" body={annotationBody} sortable sortField="label" />
                    <Column header="Evidence" body={evidenceBody} />
                    <Column header="Class" body={classBody} sortable sortField="projection_class" style={{ width: '190px' }} />
                    <Column
                        header="3D View"
                        body={(row) => (
                            row.query_pos === null || row.query_pos === undefined || !structure ? null : (
                                <Button
                                    icon="pi pi-eye"
                                    rounded
                                    text
                                    severity="info"
                                    aria-label={`Show query position ${row.query_pos} in the 3D viewer`}
                                    onClick={() => {
                                        setSelectedContact(null);
                                        setSelectedResidue({
                                            query_pos: row.query_pos,
                                            query_aa: row.query_aa,
                                            human_pos: row.human_pos,
                                            label: row.label
                                        });
                                    }}
                                />
                            )
                        )}
                        style={{ width: '90px', textAlign: 'center' }}
                    />
                </DataTable>
            </Card>
        );
    };

    const renderContacts = () => {
        if (!result) return null;

        // Contacts were asked for but the task took the sequence route: there
        // was no structure to measure. The card still appears, with the reason
        // where the rows would be - a section that vanishes silently reads as
        // a bug.
        const wanted = !!(task.params && task.params.with_contacts);
        if (!result.contacts) {
            if (!wanted) return null;
            return (
                <Card title="Contacts" className="mb-4">
                    <DataTable value={[]} size="small"
                        emptyMessage={result.structure_unavailable
                            || 'No structure found for this protein, so no contacts could be measured.'}>
                        <Column header="Type" style={{ width: '170px' }} />
                        <Column header="Atom 1" />
                        <Column header="Mapped 1" style={{ width: '160px' }} />
                        <Column header="Atom 2" />
                        <Column header="Mapped 2" style={{ width: '160px' }} />
                        <Column header="Dist. (Å)" style={{ width: '100px' }} />
                        <Column header="3D View" style={{ width: '90px' }} />
                    </DataTable>
                </Card>
            );
        }

        if (result.contacts.error) {
            return (
                <Card title="Contacts" className="mb-4">
                    <Message severity="warn" className="w-100 justify-content-start" text={result.contacts.error} />
                </Card>
            );
        }

        const c = result.contacts;

        return (
            <Card title="Contacts" className="mb-4">
                <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                    <Tag value={`Residue-Residue ${c.by_category['residue-residue'] || 0}`} severity="info" rounded />
                    <Tag value={`Ligand-Interaction ${c.by_category['ligand-interaction'] || 0}`} severity="danger" rounded />
                    <Tag value={`Modification ${c.by_category['modification'] || 0}`} severity="warning" rounded />
                    <small className="text-secondary">
                        {c.intra_chain} within the chain, {c.inter_chain} with other chains · {c.positions_in_contact} positions in contact
                    </small>
                </div>

                {!c.linked_to_projection && (
                    <Message
                        severity="warn"
                        className="w-100 justify-content-start mb-3"
                        text={c.link_warning || 'Contacts are shown without map positions.'}
                    />
                )}

                <DataTable
                    ref={contactsTable}
                    value={contactRows}
                    paginator
                    rows={5}
                    size="small"
                    emptyMessage="No contact at this threshold"
                >
                    <Column header="Type" sortable sortField="category" body={contactTypeBody} style={{ width: '170px' }} />
                    <Column header="Atom 1" sortable sortField="num_a" body={(r) => <span className="font-mono">{r.chain_a}/{r.num_a}/{r.atom_a}/{r.res_a}</span>} />
                    <Column header="Mapped 1" sortable sortField="query_pos_a" body={(r) => contactSideBody(r, 'a')} style={{ width: '160px' }} />
                    <Column header="Atom 2" sortable sortField="num_b" body={(r) => <span className="font-mono">{r.chain_b}/{r.num_b}/{r.atom_b}/{r.res_b}</span>} />
                    <Column header="Mapped 2" sortable sortField="query_pos_b" body={(r) => contactSideBody(r, 'b')} style={{ width: '160px' }} />
                    <Column field="distance" header="Dist. (Å)" sortable body={(r) => <strong>{r.distance}</strong>} style={{ width: '100px' }} />
                    <Column header="3D View" body={contactViewBody} style={{ width: '90px', textAlign: 'center' }} />
                </DataTable>
            </Card>
        );
    };

    // The key that goes with whichever colouring is selected.
    const renderLegend = () => {
        if (colourBy === 'map') {
            const ms = result.map_summary || {};
            return Object.keys(MAP_STATE_META).map((key) => (
                <div className="d-flex align-items-center mb-2" key={key}>
                    <div style={{ width: 16, height: 16, borderRadius: 3, background: MAP_STATE_META[key].colour, marginRight: 10 }} />
                    <small className="text-secondary">{MAP_STATE_META[key].label} — {ms[key] || 0}</small>
                </div>
            ));
        }

        if (colourBy === 'contacts') {
            return CONTACT_SCALE.map((step) => (
                <div className="d-flex align-items-center mb-2" key={step.label}>
                    <div style={{ width: 16, height: 16, borderRadius: 3, background: step.colour, marginRight: 10 }} />
                    <small className="text-secondary">{step.label}</small>
                </div>
            ));
        }

        return CLASS_ORDER.map((key) => (
            <div className="d-flex align-items-center mb-2" key={key}>
                <div style={{ width: 16, height: 16, borderRadius: 3, background: CLASS_META[key].colour, marginRight: 10 }} />
                <small className="text-secondary">
                    {CLASS_META[key].label} — {(result.projection_counts || {})[key] || 0}
                </small>
            </div>
        ));
    };

    const renderViewer = () => {
        if (!result || result.route !== 'structure') return null;

        if (!structure) {
            return (
                <Card title="Structure Visualization" className="mb-4">
                    <Message
                        severity="info"
                        className="w-100 justify-content-start"
                        text="The structure could not be loaded."
                    />
                </Card>
            );
        }

        return (
            <Card title="Structure Visualization" className="mb-4">
                <div className="d-flex justify-content-end mb-2">
                    <Button
                        label={expandViewer ? 'Shrink 3D view' : 'Expand 3D view'}
                        icon={expandViewer ? 'pi pi-window-minimize' : 'pi pi-window-maximize'}
                        text
                        size="small"
                        onClick={() => setExpandViewer((v) => !v)}
                    />
                </div>
                <div className="row">
                    <div className="col-md-3 mb-3">
                        <div className="bg-light rounded-3 p-3">
                            <h5 className="mb-3">Colour by</h5>
                            {COLOUR_MODES.map((mode) => (
                                <div className="d-flex align-items-center mb-2" key={mode.value}>
                                    <RadioButton
                                        inputId={`colour-${mode.value}`}
                                        name="colourBy"
                                        value={mode.value}
                                        checked={colourBy === mode.value}
                                        onChange={(e) => setColourBy(e.value)}
                                    />
                                    <label htmlFor={`colour-${mode.value}`} className="ms-2 mb-0 small">{mode.label}</label>
                                </div>
                            ))}

                            <Divider />

                            <h6 className="mb-2">Legend</h6>
                            {renderLegend()}

                            <Divider />

                            <p className="small text-secondary mb-2">
                                {selectedResidue
                                    ? `Showing query position ${selectedResidue.query_pos}.`
                                    : selectedContact
                                        ? 'Showing the contact selected in the table.'
                                        : 'Showing the whole chain.'}
                            </p>

                            {(selectedContact || selectedResidue) && (
                                <Button
                                    label="Show whole chain"
                                    icon="pi pi-globe"
                                    className="p-button-sm p-button-outlined w-100 mb-3"
                                    onClick={() => { setSelectedContact(null); setSelectedResidue(null); }}
                                />
                            )}

                            <p className="small text-secondary">
                                Use the mouse to rotate (left click), zoom (scroll) and pan (right click).
                                <br /><br />
                                More features in <a href="https://molstar.org/viewer-docs/" target="_blank" rel="noreferrer">Mol* Docs</a>.
                            </p>
                        </div>
                    </div>
                    <div className="col-md-9">
                        <div className="viewer-box border rounded"
                             style={{ height: expandViewer ? '900px' : '700px', position: 'relative' }}>
                            <MolstarViewerProjection
                                cifString={structure}
                                chain={contactsData ? contactsData.chain : 'A'}
                                colourBy={colourBy}
                                annotations={annotations || []}
                                mapRuns={mapData ? mapData.runs : []}
                                perPosition={contactsData ? contactsData.perPosition : {}}
                                contacts={contactRows}
                                selectedContact={selectedContact}
                                selectedResidue={selectedResidue}
                            />
                        </div>
                    </div>
                </div>
            </Card>
        );
    };

    const renderCandidates = () => (
        <Card className="mb-4" style={{ borderLeft: '4px solid #B9700A' }}>
            <div className="d-flex align-items-center gap-2 mb-2">
                <i className="pi pi-question-circle" style={{ color: '#B9700A' }} />
                <span className="fw-bold">Which organism is <span className="font-mono">{symbol}</span>?</span>
            </div>
            <p className="text-secondary mb-3">{candidates.length} entries match. Nothing has run yet.</p>
            {candidates.map((c) => (
                <Button key={c.accession} className="w-100 mb-2 p-button-outlined text-start" onClick={() => handleCandidatePick(c.accession)}>
                    <span className="font-mono me-3" style={{ minWidth: '90px', display: 'inline-block' }}>{c.accession}</span>
                    <span className="fst-italic me-3" style={{ minWidth: '220px', display: 'inline-block' }}>{c.organism}</span>
                    <span className="text-secondary">{c.proteinName} · {c.length} aa</span>
                </Button>
            ))}
        </Card>
    );

    const renderForm = () => (
        <>
            <Card>
                <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                    <TabPanel header="New Projection">
                        <form onSubmit={handleFormSubmit} className="mt-2">
                            <div className="row g-3">
                                <div className="col-12">
                                    <label htmlFor="projection-input" className="form-label fw-bold">Protein</label>
                                    <InputTextarea
                                        id="projection-input"
                                        value={inputText}
                                        onChange={(e) => setInputText(e.target.value)}
                                        placeholder="P0A6F5, groL, or a sequence in FASTA"
                                        className="w-100 p-inputtext-lg font-mono no-focus"
                                        style={{
                                            border: '1px solid var(--surface-400)'
                                        }}
                                        rows={inputText.includes('\n') ? 6 : 1}
                                        autoResize={false}
                                        disabled={!!selectedFile}
                                    />
                                    <div className="mt-1 small">
                                        A UniProt accession, a gene symbol or a sequence. The type is detected; nothing has to be
                                        declared. Examples:
                                        {EXAMPLES.map((ex) => (
                                            <span
                                                key={ex.label}
                                                onClick={() => setInputText(ex.value)}
                                                style={{ cursor: 'pointer' }}
                                                className="text-primary ms-2 text-decoration-underline"
                                            >
                                                {ex.label}
                                            </span>
                                        ))}
                                        <span
                                            onClick={loadingExample ? undefined : loadExampleStructure}
                                            style={{ cursor: loadingExample ? 'default' : 'pointer' }}
                                            className="text-primary ms-2 text-decoration-underline"
                                        >
                                            {loadingExample ? 'loading…' : EXAMPLE_STRUCTURE.label}
                                        </span>
                                    </div>
                                </div>

                                <div className="col-6 col-md-3">
                                    <label htmlFor="projection-taxon" className="form-label fw-bold">Organism</label>
                                    <InputNumber
                                        inputId="projection-taxon"
                                        value={taxon}
                                        onValueChange={(e) => setTaxon(e.value)}
                                        useGrouping={false}
                                        placeholder="83333"
                                        className="w-100"
                                        inputClassName="p-inputtext-lg w-100"
                                    />
                                    <div className="mt-1 small">NCBI taxon id, optional</div>
                                </div>

                                <div className="col-6 col-md-3">
                                    <label className="form-label fw-bold d-block">Or Upload (.cif)</label>
                                    <FileUpload
                                        mode="basic"
                                        name="file"
                                        accept=".cif"
                                        maxFileSize={50000000}
                                        chooseLabel={selectedFile ? 'Change' : 'Browse'}
                                        customUpload
                                        auto
                                        onSelect={(e) => setSelectedFile(e.files[0])}
                                        onClear={() => setSelectedFile(null)}
                                        className="p-button-outlined p-button-lg "
                                    />
                                    {selectedFile && (
                                        <div className="small text-success fw-bold mt-1 text-truncate">
                                            {selectedFile.name}
                                        </div>
                                    )}
                                </div>

                                <div className="col-6 col-md-3">
                                    <label htmlFor="projection-distance" className="form-label fw-bold d-block">Contacts</label>
                                    <div className="d-flex align-items-center gap-2">
                                        <Checkbox
                                            inputId="projection-contacts"
                                            checked={withContacts}
                                            onChange={(e) => setWithContacts(e.checked)}
                                        />
                                        <label htmlFor="projection-contacts" className="small mb-0 me-2">Compute</label>
                                        <InputNumber
                                            inputId="projection-distance"
                                            value={contactDistance}
                                            onValueChange={(e) => setContactDistance(e.value)}
                                            min={1}
                                            max={20}
                                            suffix=" Å"
                                            disabled={!withContacts}
                                            inputClassName="p-inputtext-lg"
                                            inputStyle={{ width: '80px' }}
                                        />
                                    </div>
                                    <div className="mt-1 small">Threshold, 1–20 Å</div>
                                </div>

                                <div className="col-6 col-md-3">
                                    <label className="form-label d-block opacity-0">Submit</label>
                                    <Button
                                        label={loading ? 'Running…' : 'Submit'}
                                        icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-play'}
                                        type="submit"
                                        disabled={loading || (!inputText.trim() && !selectedFile)}
                                        className="p-button-lg w-100"
                                    />
                                </div>
                            </div>
                        </form>
                    </TabPanel>

                    <TabPanel header="Retrieve by Task ID">
                        <div className="row g-3 align-items-end mt-2">
                            <div className="col-12 col-md-8">
                                <label htmlFor="projection-taskid" className="form-label fw-bold">Task ID</label>
                                <InputText
                                    id="projection-taskid"
                                    value={searchTaskId}
                                    onChange={(e) => setSearchTaskId(e.target.value)}
                                    placeholder="Enter UUID (e.g. f39dabe7-4478…)"
                                    className="w-100 p-inputtext-lg"
                                />
                            </div>
                            <div className="col-12 col-md-4">
                                <Button
                                    label={loading ? 'Searching…' : 'Retrieve Results'}
                                    icon="pi pi-search"
                                    onClick={retrieveTaskByid}
                                    disabled={!searchTaskId || loading}
                                    className="p-button-lg w-100 p-button-outlined"
                                />
                            </div>
                        </div>
                    </TabPanel>
                </TabView>
            </Card>
        </>
    );

    // -----------------------------------------------------------------------

    return (
        <>
            <Helmet>
                <title>Atlantis | Cross-species Projection</title>
            </Helmet>
            <Header />
            <section className="px-5 py-10">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12">
                            <h1 className="display-5 fw-bold mb-4 text-center text-sm-start">
                                Cross-species Projection
                                <sup>
                                    <a href="/help/#projection" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                        <Button
                                            icon="pi pi-info"
                                            text
                                            rounded
                                            size="small"
                                            className="btn-dialog"
                                            tooltip="Learn how the projection works"
                                        />
                                    </a>
                                </sup>
                            </h1>
                            <Toast ref={toast}></Toast>

                            {notFound && (
                                <Message
                                    severity="warn"
                                    className="w-100 justify-content-start mb-4"
                                    text="This task has expired or never existed. Results are kept for 30 days."
                                />
                            )}

                            {!task ? (
                                <>
                                    {candidates && renderCandidates()}
                                    {renderForm()}
                                </>
                            ) : (
                                <div className="fade-in">
                                    {renderStatusPanel()}

                                    {task.status === 'done' && (
                                        <>
                                            {renderTaskBar()}


                                            {!result || !result.selected_target ? (
                                                <Message
                                                    severity="info"
                                                    className="w-100 justify-content-start"
                                                    text="No human homologue found."
                                                />
                                            ) : result.human_query && humanChoice !== 'projection' ? (
                                                renderHumanQueryChoice()
                                            ) : (
                                                <>
                                                    {renderChainPicker()}
                                                    {renderSelectedTarget()}

                                                    <Card title="Residue map" className="mb-4">
                                                        <ResidueBand
                                                            mapData={mapData}
                                                            mapSummary={result.map_summary}
                                                            selected={bandRegion}
                                                            onSelect={setBandRegion}
                                                        />
                                                    </Card>

                                                    {renderCounts()}
                                                    {renderGroups()}
                                                    {renderAgreement()}
                                                    {renderAnnotations()}
                                                    {renderContacts()}
                                                    {renderViewer()}
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </>
    );
};

export default ProjectionPage;